import React, { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useOutletContext } from 'react-router-dom';

import {
  useClonalityAnalysisExecutions,
  useProjectLibSeqsWithoutSuccessfulClonalityComparisons
} from '../services/api/KeynomeAPIHooks';
import ClonalityClustersTable from './ClonalityClustersTable';
import ClonalityHeatmap from './ClonalityHeatmap';
import ClonalityHeatmapLegend from './ClonalityHeatmapLegend';
import MissingClonalitySamplesPanel from './MissingClonalitySamplesPanel';
import OrganismSelect from './OrganismSelect';
import SampleFilterList from './SampleFilterList';
import { clusterSamples } from './clusterSamples';
import { getMissingClonalitySamples } from './clonalityMissingComparisonUtils';
import {
  deriveNoReferenceTableLinksBySampleId,
  sampleIdsWithAnyNoReferencePair
} from './noReferenceComparisons';
import { useClonalityMatrix } from './useClonalityMatrix';

const UNCLASSIFIED_NAME = 'unclassified';

const isUnclassifiedName = (name) => (
  String(name || '').trim().toLowerCase() === UNCLASSIFIED_NAME
);

function ClonalityAnalysisView() {
  const { organization, activeProject } = useOutletContext();

  const { data: executions } = useClonalityAnalysisExecutions(organization?.id);
  const [selectedOrganismName, setSelectedOrganismName] = useState(null);
  const [selectedSampleNames, setSelectedSampleNames] = useState(null);
  const [showSingletons, setShowSingletons] = useState(false);
  const [activeTab, setActiveTab] = useState('clusters');

  const eligibleExecutions = useMemo(() => (
    (executions || []).filter((execution) => (
      execution.attributes.is_active && execution.attributes.status === 'succeeded'
    ))
  ), [executions]);

  const organisms = useMemo(() => {
    const organismsByName = new Map();

    eligibleExecutions.forEach((execution) => {
      const organismName = execution.attributes.organism;
      if (!organismName) {
        return;
      }

      const completedAt = execution.attributes.completed_at;
      if (!organismsByName.has(organismName)) {
        organismsByName.set(organismName, {
          name: organismName,
          latestCompletedAt: completedAt,
          executionIds: [execution.id]
        });
        return;
      }

      const existing = organismsByName.get(organismName);
      existing.executionIds.push(execution.id);

      if ((completedAt || '') > (existing.latestCompletedAt || '')) {
        existing.latestCompletedAt = completedAt;
      }
    });

    return [...organismsByName.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [eligibleExecutions]);

  const hasSelectedOrganism = useMemo(() => (
    selectedOrganismName != null && organisms.some((organism) => organism.name === selectedOrganismName)
  ), [organisms, selectedOrganismName]);

  useEffect(() => {
    if (organisms.length === 0) {
      if (selectedOrganismName != null) {
        setSelectedOrganismName(null);
      }
      return;
    }

    if (hasSelectedOrganism) {
      return;
    }

    if (organisms.length === 1) {
      setSelectedOrganismName(organisms[0].name);
      return;
    }

    const defaultOrganism = organisms.reduce((currentLatest, candidate) => {
      const currentDate = currentLatest.latestCompletedAt || '';
      const candidateDate = candidate.latestCompletedAt || '';

      if (candidateDate > currentDate) {
        return candidate;
      }

      if (candidateDate < currentDate) {
        return currentLatest;
      }

      return candidate.name.localeCompare(currentLatest.name) < 0 ? candidate : currentLatest;
    }, organisms[0]);

    setSelectedOrganismName(defaultOrganism.name);
  }, [hasSelectedOrganism, organisms, selectedOrganismName]);

  const matrix = useClonalityMatrix({
    organizationId: organization?.id,
    activeProjectId: activeProject?.id,
    organismName: hasSelectedOrganism ? selectedOrganismName : null
  });
  const {
    data: libSeqsWithoutSuccessfulComparisons,
    isLoading: isMissingClonalitySamplesLoading,
    isError: isMissingClonalitySamplesError
  } = useProjectLibSeqsWithoutSuccessfulClonalityComparisons(activeProject?.id);

  const samplesWithoutSuccessfulComparisons = useMemo(() => getMissingClonalitySamples(
    libSeqsWithoutSuccessfulComparisons,
    { activeProjectUuid: activeProject?.attributes?.uuid }
  ), [activeProject?.attributes?.uuid, libSeqsWithoutSuccessfulComparisons]);

  const seenAvailableNamesRef = useRef(new Set());

  useEffect(() => {
    if (matrix.samples.length === 0) {
      return;
    }

    const availableSampleNames = new Set(matrix.samples.map((sample) => sample.name));
    const seenAvailable = seenAvailableNamesRef.current;

    const newlyAvailable = [];
    availableSampleNames.forEach((name) => {
      if (!seenAvailable.has(name)) {
        newlyAvailable.push(name);
        seenAvailable.add(name);
      }
    });

    setSelectedSampleNames((current) => {
      const base = current == null ? new Set() : new Set(current);

      let changed = current == null;

      base.forEach((name) => {
        if (!availableSampleNames.has(name)) {
          base.delete(name);
          changed = true;
        }
      });

      newlyAvailable.forEach((name) => {
        if (!isUnclassifiedName(name)) {
          base.add(name);
          changed = true;
        }
      });

      return changed ? base : current;
    });
  }, [matrix.samples]);

  const selectedNamesForRender = useMemo(() => {
    if (selectedSampleNames != null) {
      return selectedSampleNames;
    }

    const fallback = new Set();
    matrix.samples.forEach((sample) => {
      if (!isUnclassifiedName(sample.name)) {
        fallback.add(sample.name);
      }
    });
    return fallback;
  }, [matrix.samples, selectedSampleNames]);

  const fullClusterResult = useMemo(() => (
    clusterSamples({
      samples: matrix.samples,
      comparisons: matrix.comparisons
    })
  ), [matrix.comparisons, matrix.samples]);

  const noReferenceTableLinksBySampleId = useMemo(() => (
    deriveNoReferenceTableLinksBySampleId({
      samples: matrix.samples,
      comparisons: matrix.comparisons,
      clusters: fullClusterResult.clusters,
      clusterIdBySampleId: fullClusterResult.clusterIdBySampleId,
      closelyRelatedBySampleId: fullClusterResult.closelyRelatedBySampleId,
      closelySingletonsBySampleId: fullClusterResult.closelySingletonsBySampleId
    })
  ), [
    fullClusterResult.closelyRelatedBySampleId,
    fullClusterResult.closelySingletonsBySampleId,
    fullClusterResult.clusterIdBySampleId,
    fullClusterResult.clusters,
    matrix.comparisons,
    matrix.samples
  ]);

  const alwaysVisibleTableSampleNames = useMemo(() => {
    const names = new Set();
    matrix.samples.forEach((sample) => {
      const links = noReferenceTableLinksBySampleId.get(sample.id);
      if (links?.showChip) {
        names.add(sample.name);
      }
    });
    return names;
  }, [matrix.samples, noReferenceTableLinksBySampleId]);

  const alwaysVisibleMatrixSampleNames = useMemo(() => {
    const noReferenceSampleIds = sampleIdsWithAnyNoReferencePair(
      matrix.samples,
      matrix.comparisons
    );
    const names = new Set();
    matrix.samples.forEach((sample) => {
      if (noReferenceSampleIds.has(sample.id)) {
        names.add(sample.name);
      }
    });
    return names;
  }, [matrix.comparisons, matrix.samples]);

  const singletonSampleNames = useMemo(() => {
    const clusterSizeById = new Map();
    fullClusterResult.clusters.forEach((cluster) => {
      cluster.sampleIds.forEach((sampleId) => {
        clusterSizeById.set(sampleId, cluster.size);
      });
    });

    const largestClusterSizeByName = new Map();
    const hasClonalCloseRelationByName = new Map();
    const hasSingletonCloseRelationByName = new Map();
    matrix.samples.forEach((sample) => {
      const clusterSize = clusterSizeById.get(sample.id) || 1;
      const currentSize = largestClusterSizeByName.get(sample.name) || 0;
      if (clusterSize > currentSize) {
        largestClusterSizeByName.set(sample.name, clusterSize);
      }

      const hasClonalCloseRelation = (fullClusterResult.closelyRelatedBySampleId.get(sample.id) || [])
        .some((entry) => entry.kind === 'clonal');
      if (hasClonalCloseRelation) {
        hasClonalCloseRelationByName.set(sample.name, true);
      }

      const hasSingletonCloseRelation = (
        fullClusterResult.closelySingletonsBySampleId.get(sample.id) || []
      ).length > 0;
      if (hasSingletonCloseRelation) {
        hasSingletonCloseRelationByName.set(sample.name, true);
      }
    });

    const singletonNames = new Set();
    largestClusterSizeByName.forEach((largestClusterSize, sampleName) => {
      if (
        largestClusterSize <= 1
        && !hasClonalCloseRelationByName.get(sampleName)
        && !hasSingletonCloseRelationByName.get(sampleName)
      ) {
        singletonNames.add(sampleName);
      }
    });

    return singletonNames;
  }, [
    fullClusterResult.closelyRelatedBySampleId,
    fullClusterResult.closelySingletonsBySampleId,
    fullClusterResult.clusters,
    matrix.samples
  ]);

  const effectiveSelectedSampleNames = useMemo(() => {
    if (showSingletons) {
      return selectedNamesForRender;
    }

    const filtered = new Set();
    selectedNamesForRender.forEach((sampleName) => {
      if (!singletonSampleNames.has(sampleName)) {
        filtered.add(sampleName);
      }
    });
    return filtered;
  }, [selectedNamesForRender, showSingletons, singletonSampleNames]);

  const tableSelectedSampleNames = useMemo(() => {
    const names = new Set(effectiveSelectedSampleNames);
    alwaysVisibleTableSampleNames.forEach((name) => names.add(name));
    return names;
  }, [alwaysVisibleTableSampleNames, effectiveSelectedSampleNames]);

  const matrixSelectedSampleNames = useMemo(() => {
    const names = new Set(effectiveSelectedSampleNames);
    alwaysVisibleMatrixSampleNames.forEach((name) => names.add(name));
    return names;
  }, [alwaysVisibleMatrixSampleNames, effectiveSelectedSampleNames]);

  const visibleMatrix = useMemo(() => {
    const visibleSamples = matrix.samples.filter((sample) => (
      matrixSelectedSampleNames.has(sample.name)
    ));
    const visibleIds = new Set(visibleSamples.map((sample) => sample.id));
    const visibleComparisons = new Map(
      [...matrix.comparisons.entries()].filter(([key]) => {
        const [leftId, rightId] = key.split(':').map(Number);
        return visibleIds.has(leftId) && visibleIds.has(rightId);
      })
    );

    const {
      orderedSamples,
      clusterOrderedSamples,
      clusters,
      clusterIdBySampleId,
      closelyRelatedBySampleId,
      closelySingletonsBySampleId
    } = clusterSamples({
      samples: visibleSamples,
      comparisons: visibleComparisons
    });

    return {
      samples: orderedSamples,
      comparisons: visibleComparisons,
      clusters,
      clusterIdBySampleId,
      closelyRelatedBySampleId,
      closelySingletonsBySampleId,
      clusterOrderedSamples
    };
  }, [matrixSelectedSampleNames, matrix.comparisons, matrix.samples]);

  const visibleTableMatrix = useMemo(() => {
    const tableSamples = matrix.samples.filter((sample) => (
      tableSelectedSampleNames.has(sample.name)
    ));
    const tableIds = new Set(tableSamples.map((sample) => sample.id));
    const tableComparisons = new Map(
      [...matrix.comparisons.entries()].filter(([key]) => {
        const [leftId, rightId] = key.split(':').map(Number);
        return tableIds.has(leftId) && tableIds.has(rightId);
      })
    );

    const {
      clusterOrderedSamples,
      clusters,
      clusterIdBySampleId,
      closelyRelatedBySampleId,
      closelySingletonsBySampleId
    } = clusterSamples({
      samples: tableSamples,
      comparisons: tableComparisons
    });

    const tableNoReferenceLinks = deriveNoReferenceTableLinksBySampleId({
      samples: tableSamples,
      comparisons: tableComparisons,
      clusters,
      clusterIdBySampleId,
      closelyRelatedBySampleId,
      closelySingletonsBySampleId
    });

    return {
      tableSamples: clusterOrderedSamples,
      clusters,
      closelyRelatedBySampleId,
      closelySingletonsBySampleId,
      noReferenceTableLinksBySampleId: tableNoReferenceLinks
    };
  }, [matrix.comparisons, matrix.samples, tableSelectedSampleNames]);

  const isMatrixEmpty = matrix.samples.length === 0;
  const isVisibleEmpty = visibleMatrix.samples.length === 0;
  const isTableEmpty = visibleTableMatrix.tableSamples.length === 0;
  const noCompletedResultsMessage = 'Results will appear here after a clonality analysis completes.';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'stretch',
          gap: 0
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', md: 280 },
            maxWidth: { xs: '100%', md: 280 },
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            pr: { xs: 0, md: '20px' },
            mr: { xs: 0, md: '2px' },
            borderRight: { xs: 0, md: 1 },
            borderColor: { xs: 'transparent', md: 'divider' },
            alignSelf: 'stretch',
            maxHeight: { md: '70vh' },
            minHeight: 0
          }}
        >
          <OrganismSelect
            options={organisms}
            value={hasSelectedOrganism ? selectedOrganismName : null}
            onChange={setSelectedOrganismName}
            disabled={organisms.length === 0}
          />
          <SampleFilterList
            samples={matrix.samples}
            selectedSampleNames={selectedNamesForRender}
            onChange={setSelectedSampleNames}
            showSingletons={showSingletons}
            onShowSingletonsChange={setShowSingletons}
            singletonSampleNames={singletonSampleNames}
            disabled={matrix.isLoading || matrix.samples.length === 0}
          />
          <MissingClonalitySamplesPanel
            samplesWithoutSuccessfulComparisons={samplesWithoutSuccessfulComparisons}
            isLoading={isMissingClonalitySamplesLoading}
            isError={isMissingClonalitySamplesError}
          />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2, pl: { xs: 0, md: '20px' } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(event, nextValue) => setActiveTab(nextValue)}
              aria-label="Clonality results view"
            >
              <Tab value="clusters" label="Clusters" />
              <Tab value="matrix" label="SNP matrix" />
            </Tabs>
            {!matrix.isLoading && !isVisibleEmpty && (
              <ClonalityHeatmapLegend showColors={activeTab === 'matrix'} />
            )}
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {organisms.length === 0 && (
              <Typography color="text.secondary">
                {noCompletedResultsMessage}
              </Typography>
            )}
            {organisms.length > 0 && !hasSelectedOrganism && (
              <Typography color="text.secondary">
                Select an organism to view clonality results.
              </Typography>
            )}
            {hasSelectedOrganism && matrix.isLoading && (
              <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={28} />
              </Box>
            )}
            {hasSelectedOrganism && !matrix.isLoading && isTableEmpty && isVisibleEmpty && (
              <Typography color="text.secondary">
                {isMatrixEmpty
                  ? 'No in-project clonality comparisons available for this organism in this project.'
                  : 'All samples are deselected. Use the Samples panel to select samples to compare.'}
              </Typography>
            )}
            {hasSelectedOrganism && !matrix.isLoading && !isTableEmpty && activeTab === 'clusters' && (
              <ClonalityClustersTable
                samples={visibleTableMatrix.tableSamples}
                clusters={visibleTableMatrix.clusters}
                closelyRelatedBySampleId={visibleTableMatrix.closelyRelatedBySampleId}
                closelySingletonsBySampleId={visibleTableMatrix.closelySingletonsBySampleId}
                noReferenceTableLinksBySampleId={visibleTableMatrix.noReferenceTableLinksBySampleId}
              />
            )}
            {hasSelectedOrganism && !matrix.isLoading && !isVisibleEmpty && activeTab === 'matrix' && (
              <Box sx={{ overflow: 'auto', maxHeight: '70vh' }}>
                <ClonalityHeatmap
                  samples={visibleMatrix.samples}
                  comparisons={visibleMatrix.comparisons}
                  clusters={visibleMatrix.clusters}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default ClonalityAnalysisView;
